import { useMemo, HTMLAttributes } from 'react';
import {
  AsYouType,
  isPossiblePhoneNumber,
  getExampleNumber,
} from 'libphonenumber-js';
import { CountryCode } from 'typescript/constants/localization';

import examples from 'libphonenumber-js/examples.mobile.json';
import { MarketField, MarketInputText } from '@market/react';
import { Control, Controller, RegisterOptions } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export const INPUT_PHONE_TEST_ID = 'input-phone-test-id';
export const FIELD_TEST_ID = 'field-test-id';

type ControlledInputPhoneProps = HTMLAttributes<HTMLElement> & {
  // fields used by the controller
  control: Control;
  defaultValue?: string;
  name: string;
  rules?: RegisterOptions;
  shouldUnregister?: boolean;

  // fields used by Market components, there are more that are not listed here
  // visit go/marketstorybook to see all available components and their props
  disabled?: boolean;
  label?: string;
  maxLength?: number;
  minLength?: number;

  // for the libphonenumber-js library
  countryCode: CountryCode;

  // the rules prop is not used if a custom resolver is provided
  // this will be set as false by default as most of our controlled inputs will be used in forms
  // if you are using this component outside of a form, set this to true to use the rules prop
  useRules?: boolean;
};

const ControlledInputPhone = ({
  className,
  control,
  defaultValue = '',
  name,
  rules = {},
  shouldUnregister = false,
  disabled = false,
  label,
  maxLength,
  minLength,
  countryCode,
  useRules = false,
}: ControlledInputPhoneProps) => {
  const { i18n, t } = useTranslation();

  if (useRules) {
    if (rules.required) {
      // eslint-disable-next-line no-param-reassign
      rules.validate = {
        required: (value) => Boolean(value.trim()),
      };
    }

    // eslint-disable-next-line no-param-reassign
    rules.validate = {
      ...rules.validate,
      possible: (value) => isPossiblePhoneNumber(value, countryCode),
    };
  }

  const placeholder = useMemo(
    () => getExampleNumber(countryCode, examples)?.formatNational(),
    [],
  );

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      shouldUnregister={shouldUnregister}
      {...(useRules && { rules })}
      render={({
        field: { onChange, onBlur, value },
        fieldState: { error },
        formState: { isSubmitting },
      }) => (
        <MarketField
          className={className}
          disabled={disabled || isSubmitting}
          name={name}
          invalid={Boolean(error)}
          data-testid={FIELD_TEST_ID}
        >
          <MarketInputText
            data-testid={INPUT_PHONE_TEST_ID}
            type="tel"
            name={name}
            value={value}
            placeholder={placeholder}
            maxlength={
              // require value to exist before checking length
              // since the libphonenumber-js library will throw an error if the value is not a string
              value &&
              typeof value === 'string' &&
              // test to see if this is the max possible length for a phone number by "testing" +1 length
              // courtesy of the original https://github.com/squareup/solidshop/pull/6380
              isPossiblePhoneNumber(value, countryCode) &&
              !isPossiblePhoneNumber(`${value}1`, countryCode)
                ? value.length
                : maxLength
            }
            minlength={minLength}
            onBlur={onBlur}
            onMarketInputValueChange={(e) => {
              const userInput = e.detail.value;
              let transformedInput = new AsYouType(countryCode).input(
                userInput,
              );

              if (transformedInput === value) {
                // this is a workaround to allow backspace
                // shamelessly stolen: https://gitlab.com/catamphetamine/react-phone-number-input/-/blob/master/source/InputBasic.js#L38-51
                if (transformedInput.indexOf(userInput) === 0) {
                  transformedInput = transformedInput.slice(0, -1);
                }
              }

              // eslint-disable-next-line no-param-reassign
              e.target.shadowRoot.querySelector('input').value =
                transformedInput;
              onChange(transformedInput);
            }}
          >
            <label>{label}</label>
          </MarketInputText>
          {error && (
            <small slot="error">
              {error?.message && i18n.exists(error.message)
                ? t(error.message, { label: label.toLowerCase() })
                : t('error.validation.invalidPhone')}
            </small>
          )}
        </MarketField>
      )}
    />
  );
};

export default ControlledInputPhone;
